import { Box, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { SelectedPositionLabel } from 'src/generated-types/configure-positions'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getConfigurePalette } from './configurePalette'
import { PanelBand } from './ConfigurePositionsPanel.style'

interface SelectedPositionsHeaderProps {
  selectedLabels: SelectedPositionLabel[]
  configuredCount: number
  onClear: () => void
  onUnselect: (id: number) => void
}

const SelectedPositionsHeader: React.FC<SelectedPositionsHeaderProps> = ({
  selectedLabels,
  configuredCount,
  onClear,
  onUnselect,
}) => {
  const { t } = useTranslation(['position', 'common'])

  return (
    <PanelBand dividerEdge="bottom">
      <TitleRow>
        <SelectionTitle>{t('configure-n-selected', { ns: 'position', count: selectedLabels.length })}</SelectionTitle>
        <GridActionButton
          onClick={onClear}
          buttonText={t('clear', { ns: 'common' })}
          buttonTextProps={{ fontSize: 13 }}
          border={0}
          shadowWeight={null}
        />
      </TitleRow>
      <ChipWrap>
        {selectedLabels.map(item => (
          <SelectedChip key={item.id} type="button" onClick={() => onUnselect(item.id)}>
            {item.color && <ConfiguredDot dotColor={item.color} />}
            {item.label}
            <RemoveMark aria-hidden>×</RemoveMark>
          </SelectedChip>
        ))}
      </ChipWrap>
      {configuredCount > 0 && (
        <ReplaceWarning role="status">
          {t('configure-replaces', { ns: 'position', count: configuredCount })}
        </ReplaceWarning>
      )}
    </PanelBand>
  )
}

export default SelectedPositionsHeader

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}))

const SelectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const ChipWrap = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  maxHeight: 96,
  overflow: 'auto',
}))

const SelectedChip = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.25),
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: getThemeColor(theme, MUIThemeColors.white),
  border: 0,
  borderRadius: 4,
  fontWeight: 600,
  fontSize: 12,
  padding: theme.spacing(0.25, 0.5, 0.25, 1),
  cursor: 'pointer',
  fontFamily: 'inherit',
}))

const ConfiguredDot = styled('span', { shouldForwardProp: prop => prop !== 'dotColor' })<{ dotColor: string }>(
  ({ theme, dotColor }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: dotColor,
    boxShadow: `0 0 0 1.5px ${getThemeColor(theme, MUIThemeColors.white)}`,
    marginRight: theme.spacing(0.25),
  }),
)

const RemoveMark = styled('span')({ fontSize: 14, lineHeight: 1, opacity: 0.8 })

const ReplaceWarning = styled(Box)(({ theme }) => {
  const palette = getConfigurePalette(theme)
  return {
    fontSize: 12.5,
    fontWeight: 500,
    color: palette.warningText,
    backgroundColor: palette.warningSurface,
    borderRadius: 4,
    padding: theme.spacing(1, 1.25),
  }
})
